import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Table, Badge, Avatar, Input, Space, Row, Col, Statistic, Tooltip, message } from 'antd';
import { LineChartOutlined, BarChartOutlined, DownloadOutlined, EyeOutlined, SendOutlined, LikeOutlined, MessageOutlined } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';

const { Option } = Select;
const { TextArea } = Input;

export default function EnergyDashboard() {
  const [viewType, setViewType] = useState('daily');
  const [chartType, setChartType] = useState('bar');
  const [newComment, setNewComment] = useState('');

  // Generate sample data based on view type
  const generateEnergyData = () => {
    const data = {
      daily: [2294, 6940, 8883, 9265, 8090, 8695, 7624, 5860, 8673, 7195, 8901, 12994, 18152, 6530, 9388, 7740, 6894, 14131, 14169, 8512, 7545, 7950],
      weekly: [45230, 52340, 48760, 63210, 55890, 49320, 58740, 61250, 47680, 53420, 59830, 66540],
      monthly: [185420, 198650, 205780, 223460, 195230, 189720, 207890, 215340, 191560, 203470, 218650, 225890]
    };
    return data;
  };

  const generateDates = () => {
    const now = new Date();
    const dates = {
      daily: Array.from({ length: 22 }, (_, i) => {
        const date = new Date('2025-01-07');
        date.setDate(date.getDate() + i);
        return date.toISOString().split('T')[0];
      }),
      weekly: Array.from({ length: 12 }, (_, i) => {
        const date = new Date('2024-12-30');
        date.setDate(date.getDate() + (i * 7));
        return `Week ${i + 1} - ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      }),
      monthly: Array.from({ length: 12 }, (_, i) => {
        const date = new Date('2024-01-01');
        date.setMonth(i);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      })
    };
    return dates;
  };

  const generateTableData = () => {
    const tableData = {
      daily: [
        {
          key: '1',
          date: 'Jan 28, 2025',
          usage: 7950.00,
          cost: 61245,
          peakTime: '2:30 PM',
          status: 'normal'
        },
        {
          key: '2',
          date: 'Jan 27, 2025',
          usage: 7545.00,
          cost: 58122,
          peakTime: '1:45 PM',
          status: 'normal'
        },
        {
          key: '3',
          date: 'Jan 24, 2025',
          usage: 8512.00,
          cost: 65572,
          peakTime: '3:15 PM',
          status: 'high'
        },
        {
          key: '4',
          date: 'Jan 23, 2025',
          usage: 14169.00,
          cost: 109201,
          peakTime: '2:00 PM',
          status: 'critical'
        },
        {
          key: '5',
          date: 'Jan 19, 2025',
          usage: 14131.00,
          cost: 108908,
          peakTime: '4:20 PM',
          status: 'critical'
        },
        {
          key: '6',
          date: 'Jan 18, 2025',
          usage: 6894.00,
          cost: 53114,
          peakTime: '11:30 AM',
          status: 'normal'
        }
      ],
      weekly: [
        {
          key: '1',
          date: 'Week 12 (Jan 20-26, 2025)',
          usage: 66540.00,
          cost: 512458,
          peakTime: 'Wednesday 2:30 PM',
          status: 'critical'
        },
        {
          key: '2',
          date: 'Week 11 (Jan 13-19, 2025)',
          usage: 59830.00,
          cost: 460791,
          peakTime: 'Friday 3:15 PM',
          status: 'high'
        },
        {
          key: '3',
          date: 'Week 10 (Jan 6-12, 2025)',
          usage: 53420.00,
          cost: 411334,
          peakTime: 'Tuesday 1:45 PM',
          status: 'normal'
        },
        {
          key: '4',
          date: 'Week 9 (Dec 30-Jan 5, 2025)',
          usage: 47680.00,
          cost: 367136,
          peakTime: 'Thursday 4:20 PM',
          status: 'normal'
        },
        {
          key: '5',
          date: 'Week 8 (Dec 23-29, 2024)',
          usage: 61250.00,
          cost: 471625,
          peakTime: 'Monday 11:30 AM',
          status: 'high'
        },
        {
          key: '6',
          date: 'Week 7 (Dec 16-22, 2024)',
          usage: 58740.00,
          cost: 452298,
          peakTime: 'Wednesday 2:15 PM',
          status: 'high'
        }
      ],
      monthly: [
        {
          key: '1',
          date: 'December 2024',
          usage: 225890.00,
          cost: 1739353,
          peakTime: '15th, 2:30 PM',
          status: 'critical'
        },
        {
          key: '2',
          date: 'November 2024',
          usage: 218650.00,
          cost: 1683605,
          peakTime: '22nd, 3:15 PM',
          status: 'high'
        },
        {
          key: '3',
          date: 'October 2024',
          usage: 203470.00,
          cost: 1566719,
          peakTime: '8th, 1:45 PM',
          status: 'normal'
        },
        {
          key: '4',
          date: 'September 2024',
          usage: 191560.00,
          cost: 1475012,
          peakTime: '12th, 4:20 PM',
          status: 'normal'
        },
        {
          key: '5',
          date: 'August 2024',
          usage: 215340.00,
          cost: 1658118,
          peakTime: '25th, 11:30 AM',
          status: 'high'
        },
        {
          key: '6',
          date: 'July 2024',
          usage: 207890.00,
          cost: 1600773,
          peakTime: '3rd, 2:15 PM',
          status: 'high'
        }
      ]
    };
    return tableData;
  };

  const allEnergyData = generateEnergyData();
  const allDates = generateDates();
  const allTableData = generateTableData();

  // Get current filtered data based on view type
  const currentEnergyData = allEnergyData[viewType];
  const currentDates = allDates[viewType];
  const currentTableData = allTableData[viewType];

  const chartOptions = {
    chart: {
      type: chartType,
      height: 400,
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      selection: {
        enabled: true,
        type: 'x',
        fill: {
          color: '#667eea',
          opacity: 0.1
        },
        stroke: {
          width: 1,
          dashArray: 3,
          color: '#667eea',
          opacity: 0.4
        }
      }
    },
    colors: ['#03B08D'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: currentEnergyData.length <= 30,
      formatter: function (val) {
        return val.toLocaleString();
      },
      offsetY: -20,
      style: {
        fontSize: '10px',
        colors: ['#304758']
      }
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    xaxis: {
      type: viewType === 'daily' ? 'datetime' : 'category',
      categories: viewType === 'daily' ? currentDates : currentDates,
      labels: {
        formatter: function(value) {
          if (viewType === 'daily') {
            const date = new Date(value);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }
          return value;
        }
      },
      title: {
        text: viewType === 'daily' ? 'Date' : viewType === 'weekly' ? 'Week' : 'Month'
      }
    },
    yaxis: {
      title: {
        text: 'Energy Usage (kWh)'
      },
      labels: {
        formatter: function (val) {
          return val.toLocaleString();
        }
      }
    },
    tooltip: {
      x: {
        formatter: function(value) {
          if (viewType === 'daily') {
            return new Date(value).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
          return value;
        }
      },
      y: {
        formatter: function (val) {
          return val.toLocaleString() + ' kWh';
        }
      }
    },
    grid: {
      borderColor: '#e0e6ed',
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    }
  };

  const series = [{
    name: 'Energy Usage (kWh)',
    data: viewType === 'daily'
      ? currentEnergyData.map((value, index) => ({
          x: currentDates[index],
          y: value
        }))
      : currentEnergyData.map((value, index) => ({
          x: currentDates[index],
          y: value
        }))
  }];

  const columns = [
    {
      title: viewType === 'daily' ? 'Date' : viewType === 'weekly' ? 'Week' : 'Month',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: 'Energy Usage (kWh)',
      dataIndex: 'usage',
      key: 'usage',
      render: (value) => <strong>{value.toLocaleString()}</strong>,
      sorter: (a, b) => a.usage - b.usage,
    },
    {
      title: 'Cost (₹)',
      dataIndex: 'cost',
      key: 'cost',
      render: (value) => `₹${value.toLocaleString()}`,
      sorter: (a, b) => a.cost - b.cost,
    },
    {
      title: 'Peak Time',
      dataIndex: 'peakTime',
      key: 'peakTime',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          normal: { color: 'success', text: 'Normal' },
          high: { color: 'warning', text: 'High' },
          critical: { color: 'error', text: 'Critical' }
        };
        return <Badge status={statusConfig[status].color} text={statusConfig[status].text} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Tooltip title="View Details">
          <Button icon={<EyeOutlined />} size="small" />
        </Tooltip>
      ),
    },
  ];

  // Comments data
  const [comments, setComments] = useState([
    {
      id: 1,
      author: 'House of Shipping',
      avatar: 'HS',
      date: '6/16/2025, 10:12:00 AM',
      text: "Hi! I've been monitoring the energy consumption patterns and noticed some unusual spikes during peak hours. The recent critical readings seem particularly high. Should we investigate potential equipment issues or unusual operations during that period?",
      likes: 0
    },
    {
      id: 2,
      author: 'Energy Manager',
      avatar: 'EM',
      date: '6/15/2025, 2:30:00 PM',
      text: 'Energy audit completed successfully. The overall consumption trend shows good optimization patterns. The critical readings were due to extended AC operations during system maintenance. Recommend implementing automated scheduling to prevent such spikes.',
      likes: 2
    },
    {
      id: 3,
      author: 'Facility Team',
      avatar: 'FT',
      date: '6/12/2025, 9:15:00 AM',
      text: 'Great progress on the LED lighting upgrade in Section B! The new energy-efficient lighting system has been installed and is expected to reduce consumption by 15-20%. We should see the impact in upcoming readings. Also scheduled HVAC maintenance for optimal energy efficiency.',
      likes: 5
    }
  ]);

  const handleAddComment = () => {
    if (newComment.trim()) {
      const comment = {
        id: comments.length + 1,
        author: 'Current User',
        avatar: 'CU',
        date: new Date().toLocaleString(),
        text: newComment,
        likes: 0
      };
      setComments([comment, ...comments]);
      setNewComment('');
      message.success('Comment added successfully!');
    }
  };

  const handleExport = (format) => {
    message.info(`Exporting ${viewType} data as ${format.toUpperCase()}...`);
  };

  // Calculate statistics based on current filtered chart data
  const totalUsage = currentEnergyData.reduce((sum, val) => sum + val, 0);
  const avgUsage = totalUsage / currentEnergyData.length;
  const maxUsage = Math.max(...currentEnergyData);
  const minUsage = Math.min(...currentEnergyData);

  // Calculate estimated cost (assuming ₹7.7 per kWh)

  // Get period label for statistics
  const getPeriodLabel = () => {
    switch(viewType) {
      case 'daily': return 'Daily View';
      case 'weekly': return 'Weekly View';
      case 'monthly': return 'Monthly View';
      default: return 'Current View';
    }
  };

  return (
     <div style={{
       minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <Card
          style={{
            marginBottom: '20px',
            background: 'linear-gradient(135deg,#289E80 0%,rgb(62, 197, 96) 100%)',
            border: 'none',
            color: 'white'
          }}
          bodyStyle={{ padding: '24px 32px' }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 600, margin: 0 }}>
                ME Work Energy Consumption
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', margin: '8px 0' }}>
                Real-time Energy Usage Monitoring
              </p>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: 0, maxWidth: '600px' }}>
                Track and analyze your energy consumption patterns in kilowatt-hours (kWh). Monitor usage trends, identify peak consumption periods, and optimize energy efficiency for cost-effective operations.
              </p>
            </Col>
            <Col>
              <Space>
                <Select
                  value={viewType}
                  onChange={setViewType}
                  style={{ width: 120 }}
                  dropdownStyle={{ background: 'white' }}
                >
                  <Option value="daily">Daily</Option>
                  <Option value="weekly">Weekly</Option>
                  <Option value="monthly">Monthly</Option>
                </Select>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title={`Average Usage (${getPeriodLabel()})`}
                value={avgUsage}
                precision={0}
                 valueStyle={{ color: '#52c41a' }}
               />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title={`Peak Usage (${getPeriodLabel()})`}
                value={maxUsage}
                precision={0}
                 valueStyle={{ color: '#faad14' }}
               />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title={`Total Usage (${getPeriodLabel()})`}
                value={totalUsage}
                precision={0}
                 valueStyle={{ color: '#667eea' }}
               />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title={`Minimum Usage (${getPeriodLabel()})`}
                value={minUsage}
                precision={0}
                 valueStyle={{ color: '#f5222d' }}
               />
            </Card>
          </Col>
        </Row>

        {/* Chart Section */}
        <Card
          title={
            <Space>
              <LineChartOutlined />
              Energy Consumption Chart - {getPeriodLabel()}
            </Space>
          }
          extra={
            <Space>
              <Button.Group>
                <Button
                  icon={<BarChartOutlined />}
                  type={chartType === 'bar' ? 'primary' : 'default'}
                  onClick={() => setChartType('bar')}
                >
                  Bar
                </Button>
                <Button
                  icon={<LineChartOutlined />}
                  type={chartType === 'line' ? 'primary' : 'default'}
                  onClick={() => setChartType('line')}
                >
                  Line
                </Button>
              </Button.Group>
            </Space>
          }
          style={{ marginBottom: '20px' }}
        >
          <ReactApexChart
            options={chartOptions}
            series={series}
            type={chartType}
            height={400}
          />
        </Card>

        {/* Data Table and Comments Row */}
        <Row gutter={[16, 16]}>
          {/* Data Table */}
          <Col xs={24} lg={14}>
            <Card
              title={
                <Space>
                  <span>Energy Consumption Data - {getPeriodLabel()}</span>
                </Space>
              }
              extra={
                <Space>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => handleExport('csv')}
                    size="small"
                  >
                    CSV
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => handleExport('excel')}
                    size="small"
                  >
                    Excel
                  </Button>
                </Space>
              }
            >
              <Table
                columns={columns}
                dataSource={currentTableData}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            </Card>
          </Col>

          {/* Comments Section */}
          <Col xs={24} lg={10}>
            <Card
              title={
                <Space>
                  <MessageOutlined />
                  Comments ({comments.length})
                </Space>
              }
            >
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px' }}>
                {comments.map((comment) => (
                  <div key={comment.id} style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #f0f0f0',
                    ':last-child': { borderBottom: 'none' }
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <Avatar style={{ backgroundColor: '#03B08D', flexShrink: 0 }}>
                        {comment.avatar}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <strong>{comment.author}</strong>
                          <span style={{ fontSize: '12px', color: '#999' }}>
                            {comment.date}
                          </span>
                        </div>
                        <p style={{ margin: '8px 0', lineHeight: '1.5' }}>
                          {comment.text}
                        </p>
                        <Space size="large">
                          <Button
                            type="text"
                            icon={<LikeOutlined />}
                            size="small"
                            style={{ padding: '0 4px' }}
                          >
                            {comment.likes}
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            style={{ padding: '0 4px' }}
                          >
                            Reply
                          </Button>
                        </Space>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                borderTop: '1px solid #f0f0f0',
                paddingTop: '16px'
              }}>
                <TextArea
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  style={{ marginBottom: '12px' }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    Be constructive and professional in your comments
                  </span>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                  >
                    Add Comment
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
// import React, { useState, useMemo, useEffect } from 'react';
// import ReactApexChart from 'react-apexcharts';

// const EnergyDashboard = () => {
//   const [apiData, setApiData] = useState([]);
//   const [selectedEventType, setSelectedEventType] = useState('all');
//   const [loading, setLoading] = useState(false);

//   // Sample API data structure - replace with your actual API call
//   const sampleApiData = [
//     { eventType: 'weekday', data: 180, time: '08:00', date: '2024-01-15', temperature: 42 },
//     { eventType: 'weekday', data: 190, time: '09:00', date: '2024-01-15', temperature: 45 },
//     { eventType: 'weekday', data: 200, time: '10:00', date: '2024-01-15', temperature: 48 },
//     { eventType: 'maintenance', data: 220, time: '11:00', date: '2024-01-15', temperature: 52 },
//     { eventType: 'weekday', data: 240, time: '12:00', date: '2024-01-15', temperature: 55 },
//     { eventType: 'weekday', data: 260, time: '13:00', date: '2024-01-15', temperature: 58 },
//     { eventType: 'weekday', data: 280, time: '14:00', date: '2024-01-15', temperature: 62 },
//     { eventType: 'heatwave', data: 320, time: '15:00', date: '2024-01-15', temperature: 65 },
//     { eventType: 'weekday', data: 350, time: '16:00', date: '2024-01-15', temperature: 68 },
//     { eventType: 'weekday', data: 380, time: '17:00', date: '2024-01-15', temperature: 72 },
//     { eventType: 'storm', data: 420, time: '18:00', date: '2024-01-15', temperature: 75 },
//     { eventType: 'weekday', data: 450, time: '19:00', date: '2024-01-15', temperature: 78 },
//     { eventType: 'heatwave', data: 480, time: '20:00', date: '2024-01-15', temperature: 82 },
//     { eventType: 'weekday', data: 520, time: '21:00', date: '2024-01-15', temperature: 85 },

//     { eventType: 'weekend', data: 160, time: '08:00', date: '2024-01-16', temperature: 40 },
//     { eventType: 'weekend', data: 170, time: '09:00', date: '2024-01-16', temperature: 44 },
//     { eventType: 'weekend', data: 185, time: '10:00', date: '2024-01-16', temperature: 47 },
//     { eventType: 'maintenance', data: 205, time: '11:00', date: '2024-01-16', temperature: 50 },
//     { eventType: 'weekend', data: 230, time: '12:00', date: '2024-01-16', temperature: 53 },
//     { eventType: 'weekend', data: 250, time: '13:00', date: '2024-01-16', temperature: 57 },
//     { eventType: 'weekend', data: 275, time: '14:00', date: '2024-01-16', temperature: 60 },
//     { eventType: 'storm', data: 300, time: '15:00', date: '2024-01-16', temperature: 64 },
//     { eventType: 'weekend', data: 330, time: '16:00', date: '2024-01-16', temperature: 67 },
//     { eventType: 'weekend', data: 360, time: '17:00', date: '2024-01-16', temperature: 70 },
//     { eventType: 'heatwave', data: 390, time: '18:00', date: '2024-01-16', temperature: 73 },
//     { eventType: 'weekend', data: 410, time: '19:00', date: '2024-01-16', temperature: 76 },
//     { eventType: 'weekend', data: 440, time: '20:00', date: '2024-01-16', temperature: 80 },
//     { eventType: 'weekend', data: 460, time: '21:00', date: '2024-01-16', temperature: 83 },

//     { eventType: 'holiday', data: 240, time: '08:00', date: '2024-01-17', temperature: 46 },
//     { eventType: 'holiday', data: 260, time: '09:00', date: '2024-01-17', temperature: 49 },
//     { eventType: 'holiday', data: 290, time: '10:00', date: '2024-01-17', temperature: 54 },
//     { eventType: 'maintenance', data: 320, time: '11:00', date: '2024-01-17', temperature: 59 },
//     { eventType: 'holiday', data: 350, time: '12:00', date: '2024-01-17', temperature: 63 },
//     { eventType: 'holiday', data: 380, time: '13:00', date: '2024-01-17', temperature: 66 },
//     { eventType: 'holiday', data: 400, time: '14:00', date: '2024-01-17', temperature: 69 },
//     { eventType: 'storm', data: 430, time: '15:00', date: '2024-01-17', temperature: 74 },
//     { eventType: 'holiday', data: 460, time: '16:00', date: '2024-01-17', temperature: 77 },
//     { eventType: 'heatwave', data: 490, time: '17:00', date: '2024-01-17', temperature: 81 },
//     { eventType: 'holiday', data: 520, time: '18:00', date: '2024-01-17', temperature: 84 },
//     { eventType: 'holiday', data: 550, time: '19:00', date: '2024-01-17', temperature: 87 }
//   ];

//   // Simulate API call
//   useEffect(() => {
//     setLoading(true);
//     // Replace this with your actual API call
//     setTimeout(() => {
//       setApiData(sampleApiData);
//       setLoading(false);
//     }, 500);
//   }, []);

//   // Extract unique event types from API data
//   const availableEventTypes = useMemo(() => {
//     const baseTypes = [...new Set(apiData.map(item => item.eventType))];
//     return [
//       { value: 'all', label: 'All Event Types' },
//       ...baseTypes.map(type => ({
//         value: type,
//         label: type.charAt(0).toUpperCase() + type.slice(1)
//       }))
//     ];
//   }, [apiData]);

//   // Get breakpoints based on selected event type
//   const getBreakpoints = useMemo(() => {
//     if (selectedEventType === 'all') return [];

//     // Find where the selected event type occurs and create breakpoints
//     const eventOccurrences = apiData
//       .filter(item => item.eventType === selectedEventType)
//       .map(item => item.temperature);

//     return [...new Set(eventOccurrences)].sort((a, b) => a - b);
//   }, [apiData, selectedEventType]);

//   // Process data by event type for trend lines and scatter points
//   const processedData = useMemo(() => {
//     const groupedData = {
//       weekday: [],
//       weekend: [],
//       holiday: []
//     };

//     apiData.forEach(item => {
//       const baseType = ['weekday', 'weekend', 'holiday'].includes(item.eventType)
//         ? item.eventType
//         : 'weekday'; // Default fallback

//       groupedData[baseType].push([item.temperature, item.data]);
//     });

//     // Sort each group by temperature and ensure we have data
//     Object.keys(groupedData).forEach(key => {
//       groupedData[key].sort((a, b) => a[0] - b[0]);
//     });

//     // Filter out empty groups
//     const filteredData = {};
//     Object.entries(groupedData).forEach(([key, value]) => {
//       if (value.length > 0) {
//         filteredData[key] = value;
//       }
//     });

//     return filteredData;
//   }, [apiData]);

//   // Split line data at breakpoints
//   const splitLineAtBreakpoints = (data, breakPoints) => {
//     if (breakPoints.length === 0) return [data];

//     const sortedBreakPoints = [...breakPoints].sort((a, b) => a - b);
//     const segments = [];

//     for (let i = 0; i <= sortedBreakPoints.length; i++) {
//       const startX = i === 0 ? -Infinity : sortedBreakPoints[i - 1];
//       const endX = i === sortedBreakPoints.length ? Infinity : sortedBreakPoints[i];

//       const segment = data.filter(point => {
//         const [x] = point;
//         return x > startX && x < endX;
//       });

//       if (segment.length > 1) {
//         segments.push(segment);
//       }
//     }

//     return segments;
//   };

//   // Create series data
//   const series = useMemo(() => {
//     if (apiData.length === 0) return [];

//     const seriesData = [];
//     const colors = {
//       weekday: '#ef4444',
//       weekend: '#06b6d4',
//       holiday: '#eab308'
//     };

//     const scatterColors = {
//       weekday: '#fca5a5',
//       weekend: '#67e8f9',
//       holiday: '#fde047'
//     };

//     // Create trend lines with breakpoints
//     Object.entries(processedData).forEach(([type, data]) => {
//       if (data.length > 1) {
//         const segments = splitLineAtBreakpoints(data, getBreakpoints);

//         segments.forEach((segment, index) => {
//           seriesData.push({
//             name: index === 0 ? `${type.charAt(0).toUpperCase() + type.slice(1)} Trend` : `${type} Trend ${index + 1}`,
//             type: 'line',
//             data: segment,
//             color: colors[type],
//             showInLegend: index === 0
//           });
//         });
//       }
//     });

//     // Add scatter points
//     Object.entries(processedData).forEach(([type, data]) => {
//       if (data.length > 0) {
//         seriesData.push({
//           name: `${type.charAt(0).toUpperCase() + type.slice(1)} Data`,
//           type: 'scatter',
//           data: data,
//           color: scatterColors[type]
//         });
//       }
//     });

//     return seriesData;
//   }, [processedData, getBreakpoints]);

//   const options = {
//     chart: {
//       height: 400,
//       type: 'line',
//       background: '#f8fafc',
//       toolbar: {
//         show: true
//       }
//     },
//     title: {
//       text: 'Temperature vs Riders - Event Type Analysis',
//       align: 'center',
//       style: {
//         fontSize: '18px',
//         fontWeight: 'bold'
//       }
//     },
//     stroke: {
//       width: function(opts) {
//         // Check if it's a line series by looking at the series name
//         const seriesName = opts.w.globals.seriesNames[opts.seriesIndex];
//         return seriesName.includes('Trend') ? 3 : 0;
//       },
//       curve: 'smooth'
//     },
//     markers: {
//       size: function(opts) {
//         // Check if it's a scatter series by looking at the series name
//         const seriesName = opts.w.globals.seriesNames[opts.seriesIndex];
//         return seriesName.includes('Data') ? 6 : 0;
//       },
//       strokeWidth: 0
//     },
//     xaxis: {
//       title: {
//         text: 'Temperature (°F)',
//         style: {
//           fontSize: '14px',
//           fontWeight: 'bold'
//         }
//       },
//       min: 35,
//       max: 95,
//       tickAmount: 6
//     },
//     yaxis: {
//       title: {
//         text: 'Number of Riders',
//         style: {
//           fontSize: '14px',
//           fontWeight: 'bold'
//         }
//       },
//       min: 100,
//       max: 600,
//       tickAmount: 6
//     },
//     legend: {
//       position: 'bottom',
//       horizontalAlign: 'center'
//     },
//     grid: {
//       borderColor: '#e2e8f0',
//       strokeDashArray: 0
//     },
//     annotations: {
//       xaxis: getBreakpoints.map((breakPoint, index) => ({
//         x: breakPoint,
//         borderColor: '#ef4444',
//         borderWidth: 3,
//         label: {
//           text: `${selectedEventType.charAt(0).toUpperCase() + selectedEventType.slice(1)} Event`,
//           style: {
//             color: '#fff',
//             background: '#ef4444'
//           }
//         }
//       }))
//     },
//     tooltip: {
//       custom: function({series, seriesIndex, dataPointIndex, w}) {
//         const point = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
//         return `<div class="p-2 bg-white border rounded shadow">
//           <strong>Temperature:</strong> ${point[0]}°F<br>
//           <strong>Riders:</strong> ${point[1]}<br>
//           <strong>Series:</strong> ${w.globals.seriesNames[seriesIndex]}
//         </div>`;
//       }
//     }
//   };

//   if (loading) {
//     return (
//       <div className="w-full p-6 bg-white rounded-lg shadow-lg">
//         <div className="flex items-center justify-center h-96">
//           <div className="text-lg text-gray-600">Loading chart data...</div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full p-6 bg-white rounded-lg shadow-lg">
//       {/* Event Type Dropdown */}
//       <div className="mb-6">
//         <label htmlFor="event-select" className="block text-sm font-medium text-gray-700 mb-2">
//           Break Lines by Event Type:
//         </label>
//         <select
//           id="event-select"
//           value={selectedEventType}
//           onChange={(e) => setSelectedEventType(e.target.value)}
//           className="block w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//         >
//           {availableEventTypes.map((event) => (
//             <option key={event.value} value={event.value}>
//               {event.label}
//             </option>
//           ))}
//         </select>
//       </div>

//       {/* Chart */}
//       <ReactApexChart
//         options={options}
//         series={series}
//         type="line"
//         height={400}
//       />

//     </div>
//   );
// };

// export default EnergyDashboard;
